import React, { useEffect, useState, useCallback } from 'react'
import { FlatList, View, ActivityIndicator } from 'react-native'
import { useNavigation } from '@react-navigation/native'
import styled, { useTheme } from 'styled-components/native'
import { useSelector, useDispatch } from 'APP/Hooks/redux'
import { navigationRef as Nav } from 'APP/Nav'

import { contentActions } from 'APP/Store/Content'

import Button from 'APP/Converse/Button'
import Typography from 'APP/Converse/Typography'
import Toggles from 'APP/Components/Toggles'
import CMSContentList from 'APP/Components/CMSContentList'
import Filters from 'APP/Components/ContentFilter'
import { isMobile } from 'APP/Helpers/checkPlatform'
import {
  INITIAL_LANGUAGE_FILTERS,
  INITIAL_LIBRARY_FILTERS,
  useLibraryFilters,
} from 'APP/Hooks/ContentLibrary'
import { shouldRenderCmsComponent } from 'APP/Lib/ContentHelpers'
import I18n from 'APP/I18n'
import RetryIcon from 'APP/Images/retry.svg'

import { ResponsiveScrollView } from 'APP/Converse/Layout'
import { HelpButtonComponent } from 'APP/Components/HelpButtonComponent'
import type { HealthResources, ServicesAndOptions } from 'APP/Store/Patient/types'

const Container = styled.View`
  flex: 1;
`

const EmptyWrapper = styled.View`
  display: flex;
  flex-direction: column;
  align-content: center;
  justify-content: center;
  // metrics.screenHeight is resolving to a too large number on web, so lets use the simpler vh there
  min-height: ${({ theme }) => (isMobile() ? `${theme.metrics.screenHeight - 225}px` : '90vh')};
`

const ContentWrapper = styled.View`
  padding-bottom: 100px;
`

const FilterButton = styled(Button)`
  height: auto;
  padding: ${({ theme }) => theme.metrics.baseMargin * 0.75}px;
`

const FilterDot = styled.View`
  position: absolute;
  top: ${({ theme }) => theme.metrics.baseMargin / 2}px;
  right: ${({ theme }) => theme.metrics.baseMargin / 2}px;
  background-color: ${({ theme }) => theme.colors.accent};
  width: 8px;
  height: 8px;
  border-radius: 4px;
`

const Loading = styled(ActivityIndicator)`
  flex: 1;
  justify-ontent: center;
  align-content: center;
`

const RetryWrapper = styled.View`
  display: flex;
  align-items: center;
  margin-top: ${({ theme }) => theme.metrics.baseMargin * 4}px;
`

const RetryTitle = styled(Typography)`
  margin-top: ${({ theme }) => theme.metrics.baseMargin * 2}px;
  margin-bottom: ${({ theme }) => theme.metrics.baseMargin}px;
`

const RetryButton = styled(Button)`
  margin-top: ${({ theme }) => theme.metrics.baseMargin * 2}px;
  min-width: 140px;
`

type Category = {
  id: string
  name: string
  title: string
  content_subcategories: []
}

export const shouldRenderContentSubcategory = (
  subcategory: { data: [] },
  eligibleServices: ServicesAndOptions,
  eligibleHealthResources: HealthResources
) => {
  return subcategory.data.some((item) =>
    shouldRenderCmsComponent(item, eligibleServices, eligibleHealthResources)
  )
}

export const getFilteredToggles = (
  categories: Category[],
  eligibleServices: ServicesAndOptions,
  eligibleHealthResources: HealthResources
) => {
  const filteredCategories: {
    id: string
    name: string
    title: string
    hasContent: boolean
  }[] = []
  if (categories) {
    categories.forEach((category) => {
      const filteredCategory = {
        id: category.id,
        name: category.name,
        title: category.title,
        hasContent: category.content_subcategories.some((subcategory) => {
          return shouldRenderContentSubcategory(
            subcategory,
            eligibleServices,
            eligibleHealthResources
          )
        }),
      }
      filteredCategories.push(filteredCategory)
    })
  }
  return filteredCategories
}

const CmsFeedScreen = ({ contentId }: { contentId: string }) => {
  const { colors } = useTheme()
  const navigation = useNavigation()
  const dispatch = useDispatch()

  const [filtersVisible, setFiltersVisible] = useState(false)
  const { cmsFeed } = useSelector((state) => state.content)
  const { eligibleServices, healthResources } = useSelector((state) => state.patient.profile)

  const [index, setIndex] = useState<number>()
  // @ts-expect-error content slice is not typed at all, types are inferred from initial state only
  const data = cmsFeed?.data?.[contentId]
  const error = cmsFeed?.error

  const contentCategories = data?.content_categories
  const isContentLoading = cmsFeed?.loading
  const isContentReady = !cmsFeed?.loading && contentCategories

  const library =
    isContentReady && index
      ? contentCategories?.[index].content_subcategories
      : contentCategories?.[0].content_subcategories

  const toggles = getFilteredToggles(contentCategories, eligibleServices, healthResources)

  const languageFilters = useLibraryFilters({
    baseOptions: INITIAL_LANGUAGE_FILTERS,
    library,
    eligibleServices,
    healthResources,
    persisted: true,
  })
  const currentLocale = languageFilters?.options?.[0]?.checked?.['0']

  const refetch = useCallback(() => {
    dispatch(contentActions.getCmsFeed({ contentId, locale: currentLocale }))
  }, [contentId, currentLocale, dispatch])

  useEffect(() => {
    dispatch(contentActions.getCmsFeed({ contentId, locale: currentLocale }))
  }, [contentId, currentLocale, dispatch])

  // set library to user-selected category
  const handleToggle = (index: number) => {
    setIndex(index)
  }

  const libraryFilters = useLibraryFilters({
    baseOptions: INITIAL_LIBRARY_FILTERS,
    library: languageFilters.updatedLibrary,
    eligibleServices,
    healthResources,
    persisted: false,
  })

  useEffect(() => {
    if (!cmsFeed?.loading) {
      navigation.setOptions({
        headerRight: () => (
          <>
            <FilterButton
              testID="CMS_FILTER_BTN"
              variant="tertiary"
              icon="tune-vertical-variant"
              iconVariant="material"
              onPress={() => setFiltersVisible(true)}
              widthVariant="content"
              accessibilityLabel={I18n.t('SelfNavigationScreen.searchLocation.alertButton')}
              tabIndex={0}
            />
            {/* @ts-expect-error useLibraryFilters is not typed */}
            {libraryFilters.appliedFiltersCount > 0 && <FilterDot testID="FILTER_BADGE" />}
          </>
        ),
      })
    }

    return () => {
      // @ts-expect-error params is a typed object it definitely supports being accessed
      const isMindToolkitsTab = Nav.getCurrentRoute()?.params?.tabName?.includes('Toolkits')

      navigation.setOptions({
        headerRight: isMindToolkitsTab ? () => <HelpButtonComponent /> : null,
      })
    }
  }, [cmsFeed, navigation, libraryFilters])

  if (error) {
    return (
      <RetryWrapper testID="RETRY">
        <RetryIcon fill={colors.text} />
        <RetryTitle variant="h3">{I18n.t('CmsFeedScreen.retry.title')}</RetryTitle>
        <Typography variant="bodyLarge">{I18n.t('CmsFeedScreen.retry.copy')}</Typography>
        <RetryButton
          variant="primary"
          title={I18n.t('CmsFeedScreen.retry.cta')}
          onPress={refetch}
        />
      </RetryWrapper>
    )
  }

  return (
    <Container testID="LIBRARY">
      <ResponsiveScrollView>
        {isContentReady && toggles?.length > 1 && (
          // @ts-expect-error Toggles not typed
          <Toggles testID="TOGGLES" toggles={toggles} onChange={handleToggle} />
        )}
        {isContentLoading && !isContentReady && (
          <EmptyWrapper>
            <Loading size="large" color={colors.text} testID="LOADING" />
          </EmptyWrapper>
        )}
        {isContentReady && libraryFilters.updatedLibrary && (
          <ContentWrapper>
            <View testID="LIBRARY_LIST">
              <FlatList
                testID="CATEGORY_SECTION"
                renderItem={({ item }) =>
                  item?.data?.length > 0 ? (
                    // @ts-expect-error CMSContentList not typed
                    <CMSContentList
                      key={item.name}
                      title={item.title}
                      data={item.data}
                      analyticsScreenName={'browseScreen'}
                    />
                  ) : null
                }
                data={libraryFilters.updatedLibrary}
              />
            </View>
          </ContentWrapper>
        )}
      </ResponsiveScrollView>
      {isContentReady && (
        <Filters
          visible={filtersVisible}
          languageFilters={languageFilters}
          libraryFilters={libraryFilters}
          onClose={() => setFiltersVisible(false)}
          fetching={cmsFeed.processing}
        />
      )}
    </Container>
  )
}

export default CmsFeedScreen
